import { ChapterResponse } from '@goldcast/api/content';
import {
  getAbsolutePlayerTime,
  getPreviousSegmentAbsoluteTime,
  getSegmentAtCurrentFrame,
  getSegmentTimeFromPlayerTime
} from './timeUtils';
import {
  getScreenShareSpeakerDetails,
  getSharedVideoTrackDetails
} from '@/stores/contentMetaDataStore/utils/timelineSpeakers';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { Clip } from '@/player-config/src/types/clip-types';
import { Segment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { getClipFPS, isClipAudiogram } from '@/player-config/src/utils/clipUtils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { SpeakerWithDetails } from '@/player-config/src/types/speaker-types';
import { SpeakersAnalysis } from '@/player-config/src/types/speakers-analysis';
import { VideoFeed, VideoFeedType } from '@/player-config/src/types/video-feed-types';
import { MINIMUM_SCENE_TIME_DIFFERENCE } from '@/player-config/src/constants/scene.constants';

export const buildTimelineSpeakersData = ({
  speakerDetails,
  videoFeeds,
  clip,
  speakerAnalysis
}: {
  speakerDetails: Record<string, SpeakerWithDetails>;
  videoFeeds: VideoFeed[];
  clip?: Clip;
  speakerAnalysis?: SpeakersAnalysis;
}): {
  speakers: SpeakerWithDetails[];
  videoFeeds: VideoFeed[];
  screenshareExists: boolean;
  screenShareSpeakerDetails: SpeakerWithDetails | null;
  sharedVideoTrackDetails: SpeakerWithDetails | null;
} => {
  const nonUserSpeakerIds = videoFeeds
    .filter(feed => feed.type !== VideoFeedType.USER_FEED && feed.speakerKey)
    .map(feed => feed.speakerKey);

  const speakers = Object.values(speakerDetails)
    .filter(speaker => !speaker.parent_id)
    .filter(speaker => !nonUserSpeakerIds.includes(speaker.key));

  const screenShareSpeakerDetails = getScreenShareSpeakerDetails(
    videoFeeds,
    clip,
    clip && speakerAnalysis ? { [clip.id]: speakerAnalysis } : undefined
  );
  const sharedVideoTrackDetails = getSharedVideoTrackDetails(videoFeeds);

  return {
    speakers,
    videoFeeds,
    screenshareExists: Boolean(screenShareSpeakerDetails),
    screenShareSpeakerDetails,
    sharedVideoTrackDetails
  };
};

export const MINIMUM_CHAPTER_TIME_DIFFERENCE = 3;
export const canCreateANewChapter = ({
  currentTime,
  chapters,
  clipData
}: {
  currentTime: number;
  chapters: ChapterResponse[];
  clipData: Clip;
}) => {
  const fps = getClipFPS(clipData);
  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);
  const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
  if (!currentSegment || (currentSegment.type !== SegmentType.COMPOSITE && currentSegment.type !== SegmentType.VIDEO)) {
    return false;
  }

  if (
    !isSplitsEnabled &&
    (currentTime < currentSegment.fromFrame / fps || currentTime > currentSegment.toFrame / fps)
  ) {
    return false;
  }

  const newChapterStartTime = isSplitsEnabled
    ? currentTime
    : getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);

  const hasChapterAtSamePositionOrNextToIt = chapters?.some(
    chapter =>
      chapter.start_time < newChapterStartTime + MINIMUM_CHAPTER_TIME_DIFFERENCE &&
      chapter.start_time > newChapterStartTime - MINIMUM_CHAPTER_TIME_DIFFERENCE
  );
  return !hasChapterAtSamePositionOrNextToIt;
};

export const canAddScene = ({
  compositeSegment,
  currentTime,
  clipData
}: {
  compositeSegment: CompositeSegment;
  currentTime: number;
  clipData: Clip;
}) => {
  const fps = getClipFPS(clipData);
  const trimOffset = compositeSegment.currentBounds.fromFrame;

  if (currentTime < compositeSegment.fromFrame / fps) {
    return false;
  }

  const currentSegmentFrame =
    getAbsolutePlayerTime(currentTime, clipData) * fps -
    getPreviousSegmentAbsoluteTime(compositeSegment, clipData) * fps +
    trimOffset;

  // Check if there's already a scene at this exact position
  const scenes = compositeSegment.scenes || [];
  if (isClipAudiogram(clipData)) {
    return false;
  }
  const hasSceneAtCurrentPosition = scenes.some(
    scene =>
      scene.fromFrame >= currentSegmentFrame - MINIMUM_SCENE_TIME_DIFFERENCE * fps &&
      scene.fromFrame <= currentSegmentFrame + MINIMUM_SCENE_TIME_DIFFERENCE * fps
  );

  if (
    scenes.length === 0 &&
    currentSegmentFrame - MINIMUM_SCENE_TIME_DIFFERENCE * fps <= compositeSegment.currentBounds.fromFrame
  ) {
    // no scenes then don't allow creation of scene at start which is less than MINIMUM_SCENE_TIME_DIFFERENCE * fps
    return false;
  }

  return (
    !hasSceneAtCurrentPosition &&
    currentSegmentFrame + MINIMUM_SCENE_TIME_DIFFERENCE * fps <= compositeSegment.currentBounds.toFrame
  );
};

export const getSegmentWidth = (segment: Segment) => {
  if (segment.type !== SegmentType.COMPOSITE) {
    return segment.toFrame - segment.fromFrame;
  }
  return segment.currentBounds.toFrame - segment.currentBounds.fromFrame;
};

export const prependIntroOutroDisplayText = (segment: Segment, text: string) => {
  if (segment.id === 'intro') {
    return `Intro - ${text}`;
  }
  if (segment.id === 'outro') {
    return `Outro - ${text}`;
  }
  return text;
};

const MAX_SEGMENT_DISPLAY_TEXT_LENGTH = 20;
const ELLIPSIS = '...';

const trimSegmentDisplayText = (text: string) => {
  if (text.length <= MAX_SEGMENT_DISPLAY_TEXT_LENGTH) {
    return text;
  }
  return text.slice(0, MAX_SEGMENT_DISPLAY_TEXT_LENGTH - ELLIPSIS.length) + ELLIPSIS;
};

const getVideoSrcDisplayText = (srcURL: string) => {
  const fileName = srcURL.split('/').pop()?.split('?')[0] ?? '';
  const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
  const nameWithSpaces = fileNameWithoutExtension.replaceAll(/[_\-]/g, ' ').replaceAll(/ {2,}/g, ' ');
  const cleanedNameWithSpaces = nameWithSpaces.trim();
  if (!cleanedNameWithSpaces) {
    return null;
  }
  const normalized = cleanedNameWithSpaces.charAt(0).toUpperCase() + cleanedNameWithSpaces.slice(1).toLowerCase();
  return normalized;
};

const getSegmentBaseDisplayText = (segment: Segment) => {
  if (segment.type !== SegmentType.VIDEO) {
    return segment.name;
  }

  const srcURL = segment.video.details.src;
  if (!srcURL) {
    return segment.name;
  }

  return getVideoSrcDisplayText(srcURL) ?? segment.name;
};

export const truncateDisplayText = (text: string) => {
  return trimSegmentDisplayText(text);
};

export const getSegmentDisplayText = (segment: Segment) => {
  return getSegmentBaseDisplayText(segment);
};

export const getSegmentDisplayTextWithIntroOutroPrefix = (segment: Segment) => {
  return prependIntroOutroDisplayText(segment, getSegmentDisplayText(segment));
};
